/**
 * ProfileCompletionTracker - Utility for analyzing maid profile completion
 * Provides field-by-field analysis, section progress, and missing field identification
 */

// Define required fields for each onboarding section
export const ONBOARDING_SECTIONS = {
  personalInfo: {
    title: 'Personal Information',
    icon: 'Person',
    requiredFields: [
      'firstName',
      'lastName', 
      'email',
      'phoneNumber',
      'dateOfBirth',
      'residentialAddress'
    ],
    optionalFields: [
      'languages',
      'profilePicture'
    ]
  },
  legalInsurance: {
    title: 'Legal & Insurance',
    icon: 'VerifiedUser',
    requiredFields: [
      'workAuthorized',
      'backgroundCheckConsent',
      'hasInsurance'
    ],
    conditionalFields: {
      hasInsurance: [
        'insuranceProvider',
        'policyNumber',
        'coiUploadLink'
      ]
    },
    optionalFields: [
      'idUploadLink'
    ]
  },
  experience: {
    title: 'Experience & Services',
    icon: 'Work',
    requiredFields: [
      'experienceLevel',
      'servicesOffered',
      'hasReliableTransportation'
    ],
    optionalFields: [
      'experience',
      'experienceDescription',
      'maxTravelDistance',
      'preferredServiceAreas'
    ]
  },
  availability: {
    title: 'Availability & Transport',
    icon: 'Schedule',
    requiredFields: [
      'weeklyAvailability',
      'hasVehicle',
      'hasCleaningSupplies'
    ],
    optionalFields: [
      'preferredTimeSlots',
      'hoursPerWeek',
      'noticeRequired',
      'emergencyAvailable',
      'weekendAvailable',
      'vehicleType',
      'canTransportEquipment'
    ]
  },
  preferences: {
    title: 'Preferences & Contact',
    icon: 'Settings',
    requiredFields: [
      'emergencyContactName',
      'emergencyContactRelationship', 
      'emergencyContactPhone'
    ],
    optionalFields: [
      'clientTypePreferences',
      'preferredHourlyRate',
      'comfortableWithPets',
      'willingToWorkInTeams',
      'specialSkills',
      'preferredCommunicationMethod',
      'receiveSmsNotifications',
      'emergencyContactAltPhone',
      'emergencyContactAddress'
    ]
  },
  payment: {
    title: 'Payment & Legal',
    icon: 'Payment',
    requiredFields: [
      'paymentMethod',
      'taxClassification',
      'termsOfService',
      'privacyPolicy',
      'safetyGuidelines',
      'qualityStandards',
      'backgroundCheckConsent',
      'finalAcknowledgment'
    ],
    conditionalFields: {
      paymentMethod: {
        'direct_deposit': ['bankName', 'accountType']
      }
    },
    optionalFields: [
      'marketingCommunications',
      'referralProgram'
    ]
  }
};

/**
 * Get the value of a nested field from maid profile data.
 * fieldPath may be a single dot-path or an ARRAY of fallback dot-paths
 * (first path with a defined, non-null value wins) — the data lives in
 * nested JSONB groups but older records / light API rows may carry
 * flattened aliases.
 */
const getFieldValue = (data, fieldPath) => {
  if (!data || !fieldPath) return undefined;

  const paths = Array.isArray(fieldPath) ? fieldPath : [fieldPath];

  for (const path of paths) {
    const keys = path.split('.');
    let value = data;
    for (const key of keys) {
      value = value?.[key];
      if (value === undefined || value === null) break;
    }
    if (value !== undefined && value !== null) return value;
  }

  return undefined;
};

/**
 * Check if a field value is considered "complete"
 */
const isFieldComplete = (value) => {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'boolean') return true; // Boolean fields are complete regardless of value
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
};

/**
 * Map onboarding field names to where the data actually lives on a
 * MaidProfile record. The authoritative write-side mapping is
 * `updateFromOnboardingData()` in primeshine-back/src/models/MaidProfile.js:
 * most answers are stored in nested JSONB groups (personalInfo,
 * legalDocuments, insuranceDetails, schedulePreferences, paymentInfo,
 * preferences, onboardingMeta, emergencyContact) plus a few columnar fields.
 * Each entry is an array of fallback paths — nested JSONB location first,
 * then legacy/flattened aliases so older data keeps scoring.
 */
const FIELD_MAPPING = {
  // User fields (from user object)
  firstName: ['user.firstName', 'firstName'],
  lastName: ['user.lastName', 'lastName'],
  email: ['user.email', 'email'],
  phoneNumber: ['user.phoneNumber', 'phoneNumber'],

  // personalInfo JSONB
  dateOfBirth: ['personalInfo.dateOfBirth', 'dateOfBirth'],
  residentialAddress: ['personalInfo.residentialAddress', 'residentialAddress'],
  profilePicture: ['personalInfo.profilePicture', 'profilePicture'],
  languages: ['languages'],

  // legalDocuments JSONB + columnar
  workAuthorized: ['workAuthorized'],
  backgroundCheckConsent: ['legalDocuments.backgroundCheckConsent', 'backgroundCheck', 'backgroundCheckConsent'],
  idUploadLink: ['legalDocuments.idUploadLink', 'idUploadLink'],

  // insuranceDetails JSONB + columnar
  hasInsurance: ['hasInsurance'],
  insuranceProvider: ['insuranceDetails.insuranceProvider', 'insuranceProvider'],
  policyNumber: ['insuranceDetails.policyNumber', 'policyNumber'],
  coiUploadLink: ['insuranceDetails.coiUploadLink', 'coiUploadLink'],

  // experience & services (columnar)
  experienceLevel: ['experience', 'experienceLevel'],
  experience: ['experience'],
  experienceDescription: ['serviceCapabilities.specialSkills', 'experienceDescription'],
  servicesOffered: ['services', 'servicesOffered'],
  hasReliableTransportation: ['hasVehicle', 'hasReliableTransportation'],
  maxTravelDistance: ['maxTravelRadius', 'maxTravelDistance'],
  preferredServiceAreas: ['serviceAreas', 'preferredServiceAreas'],
  specialSkills: ['serviceCapabilities.specialSkills', 'specialSkills'],

  // schedulePreferences JSONB
  weeklyAvailability: ['schedulePreferences.weeklyAvailability', 'weeklyAvailability', 'availability'],
  preferredTimeSlots: ['schedulePreferences.preferredTimeSlots', 'preferredTimeSlots'],
  hoursPerWeek: ['schedulePreferences.hoursPerWeek', 'hoursPerWeek'],
  noticeRequired: ['schedulePreferences.noticeRequired', 'noticeRequired'],
  emergencyAvailable: ['schedulePreferences.emergencyAvailable', 'emergencyAvailable'],
  weekendAvailable: ['schedulePreferences.weekendAvailable', 'weekendAvailable'],
  hasVehicle: ['hasVehicle'],
  hasCleaningSupplies: ['bringsSupplies', 'hasCleaningSupplies'],
  vehicleType: ['vehicleType'],
  canTransportEquipment: ['canTransportEquipment'],

  // emergencyContact JSON
  emergencyContactName: ['emergencyContact.name', 'emergencyContactName'],
  emergencyContactRelationship: ['emergencyContact.relationship', 'emergencyContactRelationship'],
  emergencyContactPhone: ['emergencyContact.phone', 'emergencyContactPhone'],
  emergencyContactAltPhone: ['emergencyContact.alternatePhone', 'emergencyContactAltPhone'],
  emergencyContactAddress: ['emergencyContact.address', 'emergencyContactAddress'],

  // preferences JSONB
  clientTypePreferences: ['preferences.clientTypePreferences', 'clientTypePreferences'],
  preferredHourlyRate: ['preferences.preferredHourlyRate', 'hourlyRate', 'preferredHourlyRate'],
  comfortableWithPets: ['preferences.comfortableWithPets', 'comfortableWithPets'],
  willingToWorkInTeams: ['preferences.willingToWorkInTeams', 'willingToWorkInTeams'],
  preferredCommunicationMethod: ['preferences.preferredCommunicationMethod', 'preferredCommunicationMethod'],
  receiveSmsNotifications: ['preferences.receiveSmsNotifications', 'receiveSmsNotifications'],

  // paymentInfo JSONB
  paymentMethod: ['paymentInfo.preferredPaymentMethod', 'paymentInfo.supportedMethods', 'paymentMethod'],
  bankName: ['paymentInfo.bankName', 'bankName'],
  accountType: ['paymentInfo.accountType', 'accountType'],
  taxClassification: ['paymentInfo.taxClassification', 'taxClassification'],

  // onboardingMeta JSONB (legal agreements)
  termsOfService: ['onboardingMeta.termsOfService', 'termsOfService'],
  privacyPolicy: ['onboardingMeta.privacyPolicy', 'privacyPolicy'],
  safetyGuidelines: ['onboardingMeta.safetyGuidelines', 'safetyGuidelines'],
  qualityStandards: ['onboardingMeta.qualityStandards', 'qualityStandards'],
  marketingCommunications: ['onboardingMeta.marketingCommunications', 'marketingCommunications'],
  referralProgram: ['onboardingMeta.referralProgram', 'referralProgram'],
  finalAcknowledgment: ['onboardingMeta.finalAcknowledgment', 'finalAcknowledgment']
};

/**
 * Analyze completion status for a specific section
 */
export const analyzeSectionCompletion = (maidProfile, sectionKey) => {
  const section = ONBOARDING_SECTIONS[sectionKey];
  if (!section) return null;
  
  const results = {
    sectionKey,
    title: section.title,
    icon: section.icon,
    requiredFields: [],
    optionalFields: [],
    conditionalFields: [],
    totalRequired: 0,
    completedRequired: 0,
    totalOptional: 0,
    completedOptional: 0,
    completionPercentage: 0,
    isComplete: false,
    missingRequired: [],
    missingOptional: []
  };
  
  // Analyze required fields
  section.requiredFields.forEach(fieldName => {
    const fieldPath = FIELD_MAPPING[fieldName] || fieldName;
    const value = getFieldValue(maidProfile, fieldPath);
    const isComplete = isFieldComplete(value);
    
    results.requiredFields.push({
      name: fieldName,
      path: fieldPath,
      value,
      isComplete,
      isRequired: true
    });
    
    results.totalRequired++;
    if (isComplete) {
      results.completedRequired++;
    } else {
      results.missingRequired.push({
        name: fieldName,
        path: fieldPath,
        label: getFieldLabel(fieldName)
      });
    }
  });
  
  // Analyze conditional fields
  if (section.conditionalFields) {
    Object.entries(section.conditionalFields).forEach(([triggerField, dependentFields]) => {
      const triggerPath = FIELD_MAPPING[triggerField] || triggerField;
      const triggerValue = getFieldValue(maidProfile, triggerPath);
      
      // Check if conditional fields should be required
      let shouldRequire = false;
      if (typeof dependentFields === 'object' && !Array.isArray(dependentFields)) {
        // Value-specific conditions (e.g., paymentMethod: { 'direct_deposit': [...] })
        shouldRequire = dependentFields[triggerValue] !== undefined;
        if (shouldRequire) {
          dependentFields[triggerValue].forEach(fieldName => {
            analyzeConditionalField(maidProfile, fieldName, results, true);
          });
        }
      } else if (Array.isArray(dependentFields)) {
        // Boolean conditions (e.g., hasInsurance: [...])
        shouldRequire = triggerValue === true;
        if (shouldRequire) {
          dependentFields.forEach(fieldName => {
            analyzeConditionalField(maidProfile, fieldName, results, true);
          });
        }
      }
    });
  }
  
  // Analyze optional fields
  if (section.optionalFields) {
    section.optionalFields.forEach(fieldName => {
      const fieldPath = FIELD_MAPPING[fieldName] || fieldName;
      const value = getFieldValue(maidProfile, fieldPath);
      const isComplete = isFieldComplete(value);
      
      results.optionalFields.push({
        name: fieldName,
        path: fieldPath,
        value,
        isComplete,
        isRequired: false
      });
      
      results.totalOptional++;
      if (isComplete) {
        results.completedOptional++;
      } else {
        results.missingOptional.push({
          name: fieldName,
          path: fieldPath,
          label: getFieldLabel(fieldName)
        });
      }
    });
  }
  
  // Calculate completion percentage and status
  const totalFields = results.totalRequired + results.totalOptional;
  const completedFields = results.completedRequired + results.completedOptional;
  
  results.completionPercentage = totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 100;
  results.isComplete = results.completedRequired === results.totalRequired;
  
  return results;
};

/**
 * Helper function to analyze conditional fields
 */
const analyzeConditionalField = (maidProfile, fieldName, results, isRequired) => {
  const fieldPath = FIELD_MAPPING[fieldName] || fieldName;
  const value = getFieldValue(maidProfile, fieldPath);
  const isComplete = isFieldComplete(value);
  
  results.conditionalFields.push({
    name: fieldName,
    path: fieldPath,
    value,
    isComplete,
    isRequired
  });
  
  if (isRequired) {
    results.totalRequired++;
    if (isComplete) {
      results.completedRequired++;
    } else {
      results.missingRequired.push({
        name: fieldName,
        path: fieldPath,
        label: getFieldLabel(fieldName)
      });
    }
  }
};

/**
 * Analyze completion status for the entire profile
 */
export const analyzeProfileCompletion = (maidProfile) => {
  const results = {
    overall: {
      totalSections: Object.keys(ONBOARDING_SECTIONS).length,
      completedSections: 0,
      completionPercentage: 0,
      isComplete: false
    },
    sections: {},
    allMissingRequired: [],
    allMissingOptional: [],
    completeSections: [],
    incompleteSections: []
  };
  
  // Analyze each section
  Object.keys(ONBOARDING_SECTIONS).forEach(sectionKey => {
    const sectionAnalysis = analyzeSectionCompletion(maidProfile, sectionKey);
    results.sections[sectionKey] = sectionAnalysis;
    
    if (sectionAnalysis.isComplete) {
      results.overall.completedSections++;
      results.completeSections.push(sectionKey);
    } else {
      results.incompleteSections.push(sectionKey);
    }
    
    // Aggregate missing fields
    results.allMissingRequired.push(...sectionAnalysis.missingRequired.map(field => ({
      ...field,
      section: sectionKey,
      sectionTitle: sectionAnalysis.title
    })));
    
    results.allMissingOptional.push(...sectionAnalysis.missingOptional.map(field => ({
      ...field,
      section: sectionKey,
      sectionTitle: sectionAnalysis.title
    })));
  });
  
  // Calculate overall completion
  results.overall.completionPercentage = Math.round(
    (results.overall.completedSections / results.overall.totalSections) * 100
  );
  results.overall.isComplete = results.overall.completedSections === results.overall.totalSections;
  
  return results;
};

/**
 * Get human-readable label for field names
 */
export const getFieldLabel = (fieldName) => {
  const labels = {
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email Address',
    phoneNumber: 'Phone Number',
    dateOfBirth: 'Date of Birth',
    residentialAddress: 'Residential Address',
    languages: 'Languages Spoken',
    profilePicture: 'Profile Picture',
    workAuthorized: 'Work Authorization',
    backgroundCheckConsent: 'Background Check Consent',
    hasInsurance: 'Insurance Status',
    insuranceProvider: 'Insurance Provider',
    policyNumber: 'Policy Number',
    coiUploadLink: 'Certificate of Insurance',
    idUploadLink: 'Government ID',
    experienceLevel: 'Experience Level',
    experience: 'Years of Experience',
    experienceDescription: 'Experience Description',
    servicesOffered: 'Services Offered',
    hasReliableTransportation: 'Reliable Transportation',
    maxTravelDistance: 'Max Travel Distance',
    preferredServiceAreas: 'Preferred Service Areas',
    weeklyAvailability: 'Weekly Availability',
    hasVehicle: 'Vehicle Availability',
    hasCleaningSupplies: 'Cleaning Supplies',
    emergencyContactName: 'Emergency Contact Name',
    emergencyContactRelationship: 'Emergency Contact Relationship',
    emergencyContactPhone: 'Emergency Contact Phone',
    paymentMethod: 'Payment Method',
    bankName: 'Bank Name',
    accountType: 'Account Type',
    taxClassification: 'Tax Classification',
    termsOfService: 'Terms of Service Agreement',
    privacyPolicy: 'Privacy Policy Agreement',
    safetyGuidelines: 'Safety Guidelines Agreement',
    qualityStandards: 'Quality Standards Agreement',
    marketingCommunications: 'Marketing Communications',
    referralProgram: 'Referral Program',
    finalAcknowledgment: 'Final Acknowledgment'
  };
  
  return labels[fieldName] || fieldName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
};

/**
 * Get completion status summary for display
 */
export const getCompletionSummary = (analysisResults) => {
  const { overall, allMissingRequired } = analysisResults;
  
  if (overall.isComplete) {
    return {
      status: 'complete',
      message: 'Profile is complete',
      color: 'success',
      icon: 'CheckCircle'
    };
  }
  
  if (allMissingRequired.length === 0) {
    return {
      status: 'pending_review',
      message: 'All required fields complete - pending review',
      color: 'warning',
      icon: 'HourglassEmpty'
    };
  }
  
  const missingCount = allMissingRequired.length;
  return {
    status: 'incomplete',
    message: `${missingCount} required field${missingCount === 1 ? '' : 's'} missing`,
    color: 'error',
    icon: 'Warning'
  };
};

export default {
  ONBOARDING_SECTIONS,
  analyzeSectionCompletion,
  analyzeProfileCompletion,
  getFieldLabel,
  getCompletionSummary
};